import { useCallback, useMemo } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { loginRequest, logoutRequest } from '@/features/auth/authConfig';
import { UserRole } from '@/features/auth/domain/auth.types';
import { createUserPermissions, extractRolesFromAccount, getRoleLabel } from '@/features/auth/utils/role.utils';
import { useUserPermissionsQuery } from '@/features/auth/api/authQueries';
import { authKeys } from '@/features/auth/utils/auth.utils';

/**
 * Hook for handling user login via popup
 * @returns Login function that returns success status
 */
export function useLoginPopup() {
  const {instance} = useMsal();
  const router = useRouter();
  const queryClient = useQueryClient();

  return useCallback(async (): Promise<boolean> => {
    try {
      const loginResponse = await instance.loginPopup({
        ...loginRequest,
      });

      if (loginResponse.account) {
        // Set the active account after successful login
        instance.setActiveAccount(loginResponse.account);

        // Invalidate auth queries to refresh permissions
        await queryClient.invalidateQueries({queryKey: authKeys.all});

        router.invalidate();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    }
  }, [instance, router, queryClient]);
}

/**
 * Hook for handling user logout with redirect
 */
export function useLogoutRedirect() {
  const {instance} = useMsal();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  return useCallback(async (): Promise<void> => {
    try {
      const account = instance.getActiveAccount();

      // Clear auth queries before logout
      queryClient.removeQueries({queryKey: authKeys.all});

      await instance.logoutRedirect({
        ...logoutRequest,
        account,
        onRedirectNavigate: () => {
          // Use React Router for navigation instead of MSAL's redirect
          navigate({to: "/"});
          return false; // Prevent MSAL's redirect
        },
      });
      router.invalidate();
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback navigation
      navigate({to: "/"});
    }
  }, [instance, navigate, router, queryClient]);
}

/**
 * Hook to get the current user's highest role
 * @returns The highest role the user has, or null if no roles
 */
export function useHighestRole(): UserRole | null {
  const {accounts} = useMsal();

  return useMemo(() => {
    const activeAccount = accounts[0] || null;
    const roles: UserRole[] = extractRolesFromAccount(activeAccount);

    // Return the highest role based on hierarchy: Admin > Support > Viewer
    if (roles.includes(UserRole.Admin)) return UserRole.Admin;
    if (roles.includes(UserRole.Support)) return UserRole.Support;
    if (roles.includes(UserRole.Viewer)) return UserRole.Viewer;
    return null;
  }, [accounts]);
}

/**
 * Hook to get current user account info
 * @returns Current user account or null
 */
export function useCurrentAccount() {
  const {accounts} = useMsal();
  return accounts[0] || null;
}

/**
 * Checks for user permissions based on the current query parameters
 */
export function useUserPermissions() {
  const {data: permissions, isLoading} = useUserPermissionsQuery();

  // Return empty permissions while loading
  if (isLoading || !permissions) {
    return createUserPermissions([]);
  }

  return permissions;
}

/**
 * Hook to get formatted user data for UI components
 * @returns Formatted user object or null
 */
export function useFormattedUser() {
  const user = useCurrentAccount();
  const highestRole = useHighestRole();

  return useMemo(() => {
    if (!user) return null;

    return {
      name: (user.name && user.name.length > 0) ? user.name : (user.username || ''),
      email: user.username || '',
      role: highestRole ? getRoleLabel(highestRole) : undefined
    };
  }, [user, highestRole]);
}